import { ASPIS_BACKEND_API_URL } from '@/lib/config';

export default class Endpoints {
  public static readonly suffix = '/api';

  public static readonly Auth = {
    base(): string {
      return `${Endpoints.suffix}/auth`;
    },

    /** @route POST /api/auth/register */
    register(): string {
      return `${Endpoints.Auth.base()}/register`;
    },

    /** @route POST /api/auth/quick-register */
    quickRegister(): string {
      return `${Endpoints.Auth.base()}/quick-register`;
    },

    /** @route POST /api/auth/login */
    login(): string {
      return `${Endpoints.Auth.base()}/login`;
    },

    /** @route POST /api/auth/google */
    google(): string {
      return `${Endpoints.Auth.base()}/google`;
    },

    /** @route POST /api/auth/verify-email */
    verifyEmail(): string {
      return `${Endpoints.Auth.base()}/verify-email`;
    },

    /** @route POST /api/auth/forgot-password */
    forgotPassword(): string {
      return `${Endpoints.Auth.base()}/forgot-password`;
    },

    /** @route POST /api/auth/reset-password */
    resetPassword(): string {
      return `${Endpoints.Auth.base()}/reset-password`;
    },

    /** @route POST /api/auth/refresh */
    refresh(): string {
      return `${Endpoints.Auth.base()}/refresh`;
    },
  } as const;

  public static readonly Chat = {
    base(): string {
      return `${Endpoints.suffix}/chat`;
    },

    /** @route POST /api/chat/session */
    createSession(): string {
      return `${Endpoints.Chat.base()}/session`;
    },

    /** @route POST /api/chat/message */
    sendMessage(): string {
      return `${Endpoints.Chat.base()}/message`;
    },

    /** @route GET /api/chat/session/:sessionId/messages */
    getMessages(sessionId: string): string {
      return `${Endpoints.Chat.base()}/session/${sessionId}/messages`;
    },

    /** @route GET /api/chat/history */
    getHistory(): string {
      return `${Endpoints.Chat.base()}/history`;
    },

    /** @route PATCH /api/chat/session/:sessionId */
    updateSession(sessionId: string): string {
      return `${Endpoints.Chat.base()}/session/${sessionId}`;
    },

    /** @route DELETE /api/chat/session/:sessionId */
    deleteSession(sessionId: string): string {
      return `${Endpoints.Chat.base()}/session/${sessionId}`;
    },
  } as const;

  public static readonly Portfolio = {
    base(): string {
      return `${Endpoints.suffix}/portfolio`;
    },

    /** @route POST /api/portfolio/analyze */
    analyze(): string {
      return `${Endpoints.Portfolio.base()}/analyze`;
    },

    /** @route GET /api/portfolio/analysis/history */
    getAnalysisHistory(): string {
      return `${Endpoints.Portfolio.base()}/analysis/history`;
    },

    /** @route GET /api/portfolio/analysis/:analysisId */
    getAnalysis(analysisId: string): string {
      return `${Endpoints.Portfolio.base()}/analysis/${analysisId}`;
    },

    /** @route GET /api/portfolio/analysis/:analysisId/stream */
    getAnalysisStream(analysisId: string): string {
      return `${Endpoints.Portfolio.base()}/analysis/${analysisId}/stream`;
    },

    /** @route GET /api/portfolio/analysis/:analysisId/status */
    getAnalysisStatus(analysisId: string): string {
      return `${Endpoints.Portfolio.base()}/analysis/${analysisId}/status`;
    },
  } as const;

  public static readonly Trading = {
    base(): string {
      return `${Endpoints.suffix}/trading`;
    },

    /** @route GET /api/trading/rounds/latest */
    getLatestRound(): string {
      return `${Endpoints.Trading.base()}/rounds/latest`;
    },

    /** @route GET /api/portfolio */
    getPortfolio(): string {
      return `${Endpoints.suffix}/portfolio`;
    },

    /** @route GET /api/performance */
    getPerformance(): string {
      return `${Endpoints.suffix}/performance`;
    },

    /** @route GET /api/agents/performance */
    getAgentPerformance(): string {
      return `${Endpoints.suffix}/agents/performance`;
    },
  } as const;

  public static readonly Payments = {
    base(): string {
      return `${Endpoints.suffix}/payments`;
    },

    /** @route GET /api/payments/subscription/:userId */
    getSubscription(userId: string): string {
      return `${Endpoints.Payments.base()}/subscription/${userId}`;
    },

    /** @route POST /api/orders/create */
    createOrder(): string {
      return `${Endpoints.suffix}/orders/create`;
    },

    /** @route POST /api/orders/quote */
    quoteOrder(): string {
      return `${Endpoints.suffix}/orders/quote`;
    },

    /** @route GET /api/pricing/detect-currency */
    detectCurrency(): string {
      return `${Endpoints.suffix}/pricing/detect-currency`;
    },
  } as const;

  public static readonly Account = {
    /** @route GET /api/account */
    get(): string {
      return `${Endpoints.suffix}/account`;
    },

    /** @route POST /api/subscription/cancel */
    cancelSubscription(): string {
      return `${Endpoints.suffix}/subscription/cancel`;
    },

    /** @route POST /api/subscription/resume */
    resumeSubscription(): string {
      return `${Endpoints.suffix}/subscription/resume`;
    },

    /** @route POST /api/payment-method/setup-order */
    setupCardOrder(): string {
      return `${Endpoints.suffix}/payment-method/setup-order`;
    },

    /** @route POST /api/payment-method/finalize */
    finalizeCardSetup(): string {
      return `${Endpoints.suffix}/payment-method/finalize`;
    },
  } as const;

  public static readonly Admin = {
    base(): string {
      return `${Endpoints.suffix}/admin`;
    },

    /** @route GET /api/admin/overview */
    getOverview(): string {
      return `${Endpoints.Admin.base()}/overview`;
    },

    /** @route GET /api/admin/users */
    listUsers(): string {
      return `${Endpoints.Admin.base()}/users`;
    },

    /** @route GET /api/admin/users/:userId/analytics */
    getUserAnalytics(userId: string): string {
      return `${Endpoints.Admin.base()}/users/${userId}/analytics`;
    },

    /** @route POST /api/admin/users/:userId/plan */
    changeUserPlan(userId: string): string {
      return `${Endpoints.Admin.base()}/users/${userId}/plan`;
    },

    /** @route POST /api/admin/users/:userId/impersonate */
    impersonate(userId: string): string {
      return `${Endpoints.Admin.base()}/users/${userId}/impersonate`;
    },

    /** @route GET /api/admin/organizations */
    listB2BAccounts(): string {
      return `${Endpoints.Admin.base()}/organizations`;
    },

    /** @route POST /api/admin/organizations */
    createB2BAccount(): string {
      return `${Endpoints.Admin.base()}/organizations`;
    },

    /** @route GET /api/admin/organizations/:orgId */
    getB2BAccount(orgId: string): string {
      return `${Endpoints.Admin.base()}/organizations/${orgId}`;
    },

    /** @route POST /api/admin/organizations/:orgId/seats */
    assignSeat(orgId: string): string {
      return `${Endpoints.Admin.base()}/organizations/${orgId}/seats`;
    },

    /** @route POST /api/admin/organizations/:orgId/api-keys */
    createApiKey(orgId: string): string {
      return `${Endpoints.Admin.base()}/organizations/${orgId}/api-keys`;
    },

    /** @route DELETE /api/admin/organizations/:orgId/api-keys/:keyId */
    revokeApiKey(orgId: string, keyId: string): string {
      return `${Endpoints.Admin.base()}/organizations/${orgId}/api-keys/${keyId}`;
    },

    /** @route GET /api/admin/audit-log */
    listAuditLog(): string {
      return `${Endpoints.Admin.base()}/audit-log`;
    },

    /**
     * @route GET {backend-v2}/api/b2b/admin/organizations/:orgId/funds
     * Absolute URL — org vaults live in Aspis backend-v2, not this provider. The shared
     * axios client still attaches the admin bearer (absolute URLs bypass baseURL only).
     */
    listOrgVaults(orgId: string): string {
      return `${ASPIS_BACKEND_API_URL}/b2b/admin/organizations/${orgId}/funds`;
    },
  } as const;
}
